import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { errorMessages } from '../../../utils/methods';
import {
  classDeleteIdOf,
  deleteNonClassSchedule,
  forceDeleteClass,
  getClassDeletePreview,
} from '../../../api/scheduleDelete';

const NON_CLASS_KINDS = ['CONSULTATION', 'ETC'];

/**
 * 스케줄 카드 우클릭 '일정 삭제'. 지점 스케줄과 그룹 스케줄이 같이 쓴다.
 * 수업: 삭제 미리보기 → 확인 모달 → 강제 삭제(restoreUsage). 상담·기타: 확인 모달 → 기존 삭제 API.
 * 성공하면 개인·그룹 스케줄 목록, 상담 이력, 회원 수강권 목록을 갱신한다.
 */
export const useScheduleDelete = ({ token, toast }) => {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState(null);
  const [busy, setBusyState] = useState(false);
  // 연타 방지 — state 는 다음 렌더에야 반영되므로 ref 로 즉시 막는다
  const busyRef = useRef(false);
  const setBusy = value => {
    busyRef.current = value;
    setBusyState(value);
  };

  const request = useCallback(
    async event => {
      if (!event) return;
      if (NON_CLASS_KINDS.includes(event.courseClassType)) {
        setTarget({ kind: event.courseClassType, event });
        return;
      }
      const id = classDeleteIdOf(event);
      if (!id) return;
      setTarget({ kind: 'CLASS', event });
      try {
        const preview = await getClassDeletePreview(id, token);
        // 그사이 닫았거나 다른 카드를 열었으면 무시
        setTarget(prev => (prev?.event === event ? { ...prev, preview: preview || {} } : prev));
      } catch (error) {
        setTarget(prev => (prev?.event === event ? null : prev));
        toast.error(errorMessages(error));
      }
    },
    [toast, token],
  );

  const confirm = async restoreUsage => {
    if (!target || busyRef.current) return;
    setBusy(true);
    try {
      if (target.kind === 'CLASS') {
        await forceDeleteClass(classDeleteIdOf(target.event), restoreUsage, token);
      } else {
        await deleteNonClassSchedule(target.event, token);
      }
      toast.success(restoreUsage ? '횟수를 돌려주고 일정을 삭제했습니다' : '일정을 삭제했습니다');
      setTarget(null);
      // 스케줄 목록 + 상담 이력(기존 상담 삭제 경로와 같은 키) + 수강권 잔여(횟수 복원·출결 취소 반영)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['privateLessons'] }),
        queryClient.invalidateQueries({ queryKey: ['groupLessons'] }),
        queryClient.invalidateQueries({ queryKey: ['consultationList'] }),
        queryClient.invalidateQueries({ queryKey: ['memberTickets'] }),
      ]);
    } catch (error) {
      // 406(삭제 불가)·403(권한) 등은 서버 메시지 그대로. 모달은 열어 둔다
      toast.error(errorMessages(error));
    } finally {
      setBusy(false);
    }
  };

  return { target, busy, request, confirm, close: () => !busyRef.current && setTarget(null) };
};
